/**
 * useStaffAvailability — one person's RECURRING weekly pattern, read and
 * written through the typed client (`getStaffAvailability` /
 * `setStaffAvailability`, contract 2026-09-11).
 *
 * No seed data in the UI, and no local-only edit: save PUTs the whole set and
 * re-renders from the response, so a rejected write shows as a failure rather
 * than as a row that quietly disappears on reload. `timezone` and `seeded`
 * ride out of the hook untouched — the screen has to be able to say "stated in
 * Europe/Zurich" and to tell "no pattern recorded yet" from "no fixed hours".
 *
 * `loaded` is true only once a read for THIS staff id has landed. Until then
 * `timezone` is null for a different reason than "none recorded", so a screen
 * must not say "no time zone" (or render a converted date) off it — it shows
 * its skeleton instead. A record that belongs to a previous staff id is never
 * returned for the current one.
 */
import { useCallback, useEffect, useState } from "react";
import { getStaffAvailability, setStaffAvailability } from "@v0y4ge/api-client";
import type { StaffAvailabilityEnvelope, StaffAvailabilityRule } from "@v0y4ge/api-contracts";

export type AvailabilityRule = StaffAvailabilityRule;
export const WEEKDAYS: AvailabilityRule["day"][] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
export const WEEKDAY_LABEL: Record<string, string> = {
  Mon: "Monday", Tue: "Tuesday", Wed: "Wednesday", Thu: "Thursday",
  Fri: "Friday", Sat: "Saturday", Sun: "Sunday",
};

/* Legacy IANA names a runtime may still report (V8/ICU answer "Asia/Calcutta"
   for a browser in India). Display only: the stored value is never rewritten,
   and anything not listed is shown as written. */
const LEGACY_ZONE_NAMES: Record<string, string> = { // ui-config
  "Asia/Calcutta": "Asia/Kolkata", "Asia/Katmandu": "Asia/Kathmandu", "Asia/Saigon": "Asia/Ho_Chi_Minh",
  "Asia/Rangoon": "Asia/Yangon", "Asia/Dacca": "Asia/Dhaka", "Asia/Thimbu": "Asia/Thimphu",
  "Asia/Ulan_Bator": "Asia/Ulaanbaatar", "Asia/Macao": "Asia/Macau", "Asia/Ujung_Pandang": "Asia/Makassar",
  "Europe/Kiev": "Europe/Kyiv", "America/Godthab": "America/Nuuk", "Atlantic/Faeroe": "Atlantic/Faroe",
  "Pacific/Truk": "Pacific/Chuuk", "Pacific/Ponape": "Pacific/Pohnpei", "Pacific/Enderbury": "Pacific/Kanton",
  "America/Buenos_Aires": "America/Argentina/Buenos_Aires", "America/Catamarca": "America/Argentina/Catamarca",
  "America/Cordoba": "America/Argentina/Cordoba", "America/Jujuy": "America/Argentina/Jujuy",
  "America/Mendoza": "America/Argentina/Mendoza", "America/Indianapolis": "America/Indiana/Indianapolis",
  "America/Louisville": "America/Kentucky/Louisville",
};

/** The canonical IANA name to PRINT for a zone (Asia/Calcutta → Asia/Kolkata). */
export function displayZoneName(tz: string | null | undefined): string {
  const zone = String(tz ?? "").trim();
  return LEGACY_ZONE_NAMES[zone] ?? zone;
}

function knownZone(tz: string | null | undefined): string | null {
  const zone = String(tz ?? "").trim();
  if (!zone) return null;
  try { new Intl.DateTimeFormat("en-US", { timeZone: zone }).format(); return zone; } catch { return null; }
}

/**
 * Where the zone the availability read answered came from.
 *
 * The read labels a pattern with the zone it is kept in: the zone the hours
 * were authored in, else the person's own zone, else the zone of the venue
 * (node) they belong to (`workers/_shared/staff-rule-zone.ts`). The envelope
 * does not say which, so the caller passes the person's OWN zone from the
 * roster record (`CanonicalStaffMember.timezone`) and this compares the two:
 *
 *  - `own`      the person has a zone and the hours are shown in it;
 *  - `venue`    the person has no zone of their own and no saved hours, so the
 *               server can only have answered with the venue's zone;
 *  - `authored` the person has a zone, but these hours were set in another;
 *  - `unknown`  the source cannot be established: the caller could not say what
 *               the person's own zone is, or the person has no zone of their own
 *               but has saved hours, which may carry the zone they were set in
 *               rather than the venue's — so the screen states the zone neutrally.
 */
export type ZoneSource = "own" | "venue" | "authored" | "unknown";
export function zoneSource(effective: string | null, own: string | null | undefined, hasSavedHours: boolean): ZoneSource {
  if (own === undefined) return "unknown";
  const mine = knownZone(own);
  if (!mine) return hasSavedHours ? "unknown" : "venue";
  return displayZoneName(mine) === displayZoneName(effective) ? "own" : "authored";
}

export interface StaffAvailabilityState {
  rules: AvailabilityRule[];
  timezone: string | null;
  /** A read for THIS staff id has landed. False while the first read is in
   *  flight and after a failed one — never "no zone recorded". */
  loaded: boolean;
  /** False means nobody has recorded a pattern for this person yet. */
  seeded: boolean;
  loading: boolean;
  /** Set only when the READ failed — never for an empty pattern. */
  error: string | null;
  saving: boolean;
  /** Replaces the whole set. Resolves true on a saved write. */
  save: (next: AvailabilityRule[]) => Promise<boolean>;
  /** Surfaced so the caller can show the reason a save was refused. */
  saveError: string | null;
  reload: () => void;
}

export function useStaffAvailability(staffId: string | null): StaffAvailabilityState {
  /* Keyed by the staff id it was read for, so switching person never shows the
     previous person's zone or hours while the next read is in flight. */
  const [env, setEnv] = useState<{ staffId: string; data: StaffAvailabilityEnvelope } | null>(null);
  const [loading, setLoading] = useState(!!staffId);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let live = true;
    if (!staffId) { setEnv(null); setLoading(false); setError(null); return; }
    setLoading(true);
    setError(null);
    getStaffAvailability(staffId)
      .then((e) => { if (live) { setEnv({ staffId, data: e }); setLoading(false); } })
      .catch((e: unknown) => {
        if (!live) return;
        console.warn("getStaffAvailability failed:", e);
        setError("Couldn't load this person's availability.");
        setLoading(false);
      });
    return () => { live = false; };
  }, [staffId, nonce]);

  const save = useCallback(async (next: AvailabilityRule[]) => {
    if (!staffId) return false;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await setStaffAvailability(staffId, next);
      setEnv({ staffId, data: saved });
      return true;
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [staffId]);

  const current = env && env.staffId === staffId ? env.data : null;
  return {
    rules: current?.rules ?? [],
    timezone: current?.timezone ?? null,
    loaded: current !== null,
    seeded: current?.seeded ?? false,
    loading,
    error,
    saving,
    save,
    saveError,
    reload: () => setNonce((n) => n + 1),
  };
}
